import type { CSSProperties } from 'react'
import { Button, Table, Tag } from '@grovus/design-system'
import type { AdminCopy } from '../i18n'
import type { AdminUser } from './api'
import { daysUntil, formatWhen } from './format'

export type UsersView =
  | { status: 'ok'; users: AdminUser[] }
  | { status: 'loading' }
  | { status: 'failed' }

interface Props {
  copy: AdminCopy
  view: UsersView
  narrow: boolean
  onRetry: () => void
}

const BORDER = '1px solid var(--mantine-color-default-border)'
const FRAME: CSSProperties = { border: BORDER, borderRadius: 'var(--mantine-radius-lg)' }
const TOP: CSSProperties = { verticalAlign: 'top' }
const SKELETON = 'light-dark(var(--mantine-color-ink-1), var(--mantine-color-dark-5))'
const SKELETON_ROWS = [['210px', '150px', '90px', '180px'], ['170px', '120px', '110px', '140px'], ['230px', '150px', '80px', '200px']]

function Bar({ width }: { width: string }) {
  return <div style={{ height: 12, width, maxWidth: '100%', borderRadius: 'var(--mantine-radius-sm)', background: SKELETON }} />
}

function States({ user, copy }: { user: AdminUser; copy: AdminCopy }) {
  const tags: [string, string][] = []
  if (user.platformAdmin) tags.push([copy.statePlatformAdmin, 'ink'])
  if (user.disabled) tags.push([copy.stateDisabled, 'bad'])
  if (user.canCreateCompanies) tags.push([copy.stateApproved, 'brand'])
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 6 }}>
      {tags.map(([label, color]) => <Tag key={label} color={color}>{label}</Tag>)}
      {tags.length === 0 && <span className="grv-muted">{copy.admittedOnly}</span>}
    </div>
  )
}

function SignIn({ user, copy }: { user: AdminUser; copy: AdminCopy }) {
  return user.lastSignedInAt === null
    ? <span className="grv-muted">{copy.noSignin}</span>
    : <span className="grv-num">{formatWhen(user.lastSignedInAt, copy)}</span>
}

function Clients({ user, copy }: { user: AdminUser; copy: AdminCopy }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      {user.companies.map((c) => (
        <span key={c.id} style={{ overflowWrap: 'anywhere' }}>{`${c.name} · ${copy.roles[c.role]}`}</span>
      ))}
      {user.pendingInvites.map((i) => (
        <span key={i.companyId} className="grv-muted" style={{ fontSize: 'var(--mantine-font-size-sm)', textWrap: 'pretty', overflowWrap: 'anywhere' }}>
          {copy.invited(i.companyName, i.role, daysUntil(i.expiresAt))}
        </span>
      ))}
      {user.companies.length === 0 && user.pendingInvites.length === 0 && <span className="grv-muted">{copy.noClients}</span>}
    </div>
  )
}

/** Screen 12: everyone admitted, read-only. A table from 768 px, stacked rows below. */
export function UsersPage({ copy, view, narrow, onRetry }: Props) {
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--mantine-font-size-xl)', fontWeight: 700, letterSpacing: '-0.01em' }}>{copy.usersTitle}</h2>
          {view.status === 'ok' && (
            <span className="grv-muted grv-num" style={{ fontSize: 'var(--mantine-font-size-xl)', fontWeight: 500 }}>{view.users.length}</span>
          )}
        </div>
        <span className="grv-muted">{copy.usersSub}</span>
      </div>

      {view.status === 'failed' && (
        <div style={{ ...FRAME, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px 16px', padding: 24 }}>
          <span className="grv-muted" role="alert">{copy.usersFailed}</span>
          <Button variant="default" onClick={onRetry}>{copy.retry}</Button>
        </div>
      )}

      {view.status !== 'failed' && !narrow && (
        <div style={FRAME}>
          <Table aria-busy={view.status === 'loading'}>
            <colgroup><col /><col style={{ width: 230 }} /><col style={{ width: 140 }} /><col style={{ width: 160 }} /><col style={{ width: 310 }} /></colgroup>
            <Table.Thead>
              <Table.Tr>
                <Table.Th><span className="grv-label">{copy.email}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.state}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.admitted}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.signin}</span></Table.Th>
                <Table.Th><span className="grv-label">{copy.clients}</span></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {view.status === 'ok' && view.users.map((user) => (
                <Table.Tr key={user.id}>
                  <Table.Td style={TOP}><span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{user.email}</span></Table.Td>
                  <Table.Td style={TOP}><States user={user} copy={copy} /></Table.Td>
                  <Table.Td style={TOP}><span className="grv-num">{formatWhen(user.admittedAt, copy)}</span></Table.Td>
                  <Table.Td style={TOP}><SignIn user={user} copy={copy} /></Table.Td>
                  <Table.Td style={TOP}><Clients user={user} copy={copy} /></Table.Td>
                </Table.Tr>
              ))}
              {view.status === 'loading' && SKELETON_ROWS.map(([a, b, c, d]) => (
                <Table.Tr key={a + b + c + d}>
                  <Table.Td style={TOP}><Bar width={a} /></Table.Td>
                  <Table.Td style={TOP}><Bar width={b} /></Table.Td>
                  <Table.Td style={TOP}><Bar width="70px" /></Table.Td>
                  <Table.Td style={TOP}><Bar width={c} /></Table.Td>
                  <Table.Td style={TOP}><Bar width={d} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </div>
      )}

      {view.status !== 'failed' && narrow && (
        <div style={{ ...FRAME, display: 'flex', flexDirection: 'column' }} aria-busy={view.status === 'loading'}>
          {view.status === 'ok' && view.users.map((user, i) => (
            <div key={user.id} style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '12px 16px', borderTop: i === 0 ? 'none' : BORDER }}>
              <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{user.email}</span>
              <States user={user} copy={copy} />
              <div style={{ display: 'grid', gridTemplateColumns: '92px minmax(0,1fr)', gap: '6px 12px', alignItems: 'baseline' }}>
                <span className="grv-label">{copy.admitted}</span>
                <span className="grv-num">{formatWhen(user.admittedAt, copy)}</span>
                <span className="grv-label">{copy.signin}</span>
                <div><SignIn user={user} copy={copy} /></div>
                <span className="grv-label">{copy.clients}</span>
                <Clients user={user} copy={copy} />
              </div>
            </div>
          ))}
          {view.status === 'loading' && SKELETON_ROWS.map(([a, b, c], i) => (
            <div key={a + b + c} style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '12px 16px', borderTop: i === 0 ? 'none' : BORDER }}>
              <Bar width={a} /><Bar width={b} /><Bar width={c} />
            </div>
          ))}
        </div>
      )}
    </>
  )
}
